import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from "next-seo";
import { Message } from "semantic-ui-react";
import Link from "next/link";

const BaliArticle = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/beaten-track-bali`;

  return (
    <MainLayout>
      <NextSeo
        title='8 Things To Do Off-The-Beaten-Track In Bali | DAYMADE'
        description='Ditch the tourists, influencers and #balivibes and discover the real & authentic Bali through these 8 places off the beaten track.'
        openGraph={{
          title: '8 Things To Do Off-The-Beaten-Track In Bali | DAYMADE',
          description: 'Ditch the tourists, influencers and #balivibes and discover the real & authentic Bali through these 8 places off the beaten track.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/8-things-to-do-off-the-beaten-track-in-bali',
          images: [{
            url: `${IMAGE_DIRECTORY}/hero.jpg`,
            alt: 'woman sitting in infinity pool overlooking forest area with palm trees and large cabins'
          }]
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'instagram,instagrammable,bali,competition,beach,travel,destinations,holiday,prizes,getaway'
        // }]}
      />
      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText='woman sitting in infinity pool overlooking forest area with palm trees and large cabins'
        heroImageYPosition='100%'
      >
        <>
          <h1 style={{ fontSize: '1.6em' }}>
            8 Things To Do Off-The-Beaten-Track <strong>In Bali</strong>
          </h1>

          <div className='separator' style={{ width: '80%', marginTop: 10 }} />

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '2em' }}>
            Less is often more when travelling, especially when you’re looking for the spots where fewer tourists wander. While it’s true that Bali is relentlessly on trend, most people flock to the south of this popular Indonesian island (hey, we’re not judging! Been there, done that and got the t-shirt). The good news is that these tourist magnets leave plenty of room to explore Bali in all the other cardinal directions – sometimes even without another tourist in sight.
          </p>

          <p>
            <span className='bigFirstLetter'>S</span>unrise at the top of Bali’s Mount Batur volcano is definitely worth a trip, but this gorgeous island is so much more than the popular sight-seeing hot spots that attract mainstream tourism. Few travelers actually venture off the beaten path when in Bali, which is great because it gives adventurous jetsetters (like yourself!) the chance to explore the rest of the island in peace. As one of the world’s most famous resort islands, Bali may seem like an unlikely choice to escape the crowds, but DAYMADE is here to help you bypass the masses and experience your own authentic adventure when travelling around this green paradise.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt='farmer carrying large baskets of crops across terraced farmland in Bali'
            className='singleBlogImage'
          />

          <h4>
            Jatiluwih Rice Terraces
          </h4>

          <p>
            A trip to Bali wouldn’t be complete without visiting the infamous rice shelves. While the extremely popular and overrun Tegalalang rice terraces draw loads of tourists to its fields, the Jatiluwih rice terraces are among the largest rice paddies in Bali and are truly underappreciated (hallelujah!). You won’t run into many people here except for a few locals and visitors from nearby resorts. The rice plants extend as far as the eye can see, giving you the feel that it’s just you and nature. Pictures simply do not do this wildly lush green landscape justice: go see for yourself one of the few places in the world where even Photoshop can’t make look better.
          </p>

          <h4>
            Mountain Bike Tours
          </h4>

          <p>
            Some places have become all but synonymous with a certain kind of experience: for Bali, it’s surfing, yoga and being a digital nomad. Mountain biking is not usually one of them. But now you know! Cruising through the vibrant, hilly landscape is Bali’s secret hobby that you won’t want to miss. Bali is definitely an under-the-radar choice for mountain bikers, and the terrain is sure to satisfy your need for speed (and gorgeous landscapes).
          </p>

          <h4>
            Lombok
          </h4>

          <p>
            Bali can really feel like a bubble and it’s easy to forget that there are plenty of other nearby islands to explore. Lombok is often referred to as Indonesia’s second Bali – a short boat ride’s distance away and known for its secret beaches. Most people venture over to the Gili Islands, the three islands that put Lombok on the tourist map. Since these three little tourist traps have more popularity than the main island itself, do yourself a favour: skip the touristy trio and head over to Lombok’s mainland for a truly authentic experience.
          </p>

          <p>
            Due to its history (both islands were once under the King of Karangasem from Bali), you can still find a lot of Balinese influence on Lombok, while still experiencing the island’s very own distinct flair. Lombok’s tourism is still in its infancy compared to Bali and is an idyllic location sure to fulfill that beach island life you’re seeking.
          </p>

          <div className='doubleImageContainer'>
            <img
              src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
              alt='peaceful, open-sided huts dotted throughout calm water with large mountain/volcano in distance'
              className='doubleBlogImage'
            />

            <img
              src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
              alt='women wearing religious head-wear, leaving temple in Bali during a ceremony'
              className='doubleBlogImage'
            />
          </div>

          <h4>
            Pink Beaches
          </h4>

          <p>
            A former fishing village, Tangsi Beach is one of Lombok’s “secret” beaches because of the phenomenon that’s rarely found on other beaches: the sand is pink. The pink colour of the sand comes from the organisms on the coral reefs (called Homtrema Rubrum). It’s a wonderful beach to go and swim, snorkel, ride a boat to the small islands nearby or simply enjoy the calm sea waters. The highlight is the Pink Beach viewpoint, a hill found at the end of the beach which you can walk up and soak in stunning panoramic views from above.
          </p>

          <p>
            If you’re into seeing as many of the world’s few pink beaches as possible, another impressive pink beach is hidden just a few islands away from Bali. Komodo (home to the famous and endangered Komodo Dragon) is Lombok’s more popular pink-beach sister. The entire island is a great place to visit and skip the hustle and bustle from Bali.
          </p>

          <h4>
            Black Sand Beaches
          </h4>

          <p>
            As if Indonesia didn’t have enough colourful beaches! Black sand beaches in Bali can be found all along the east coast stretching from Padang Bai in the south to Lovina in the north. Amed is a charming fishing village where you can experience black sand beaches and pristine water at its finest. The black sand is believed to have originated from the lava from Bali's most famous and sacred volcano, Mount Agung. Amed or one of the surrounding villages are a great jump-off point before heading over to Lombok as part of your flying-under-the-radar trip on and around Bali.
          </p>

          <h4>
            Nyepi Day
          </h4>

          <p>
            Bali isn’t only beaches, mountains and rice fields. The island is home to a rich culture of rituals, traditions, superstitions and celebrations. Whether you know the specific holiday or event being celebrated or not, it’s impossible to skim over the fact that Balinese people are constantly in ritual.
          </p>

          <p>
            Nyepi Day, or the Day of Silence, is a Hindu Balinese holiday which is usually celebrated in March. The day following Nyepi is also celebrated as New Year's Day. It’s a week-long celebration involving different rituals: first the Melasti Ritual, second the Bhuta Yajna Ritual, and finally the Nyepi Day Rituals starting at 6:00 a.m. and lasting until 6:00 a.m. the next day. On this day, the Day of Silence, everything stops for 24 hours – none or only low lights may be turned on, some people even fast and go into silence. This series of rituals and celebrations are held to restore the balance between good and evil, gods, humans and nature. The myth goes that demons and gods fly over the island during these 24 hours but are fooled into believing that Bali is uninhabited because it’s completely dark and silent.
          </p>

          <p>
            Some visitors feel like being on Bali over Nyepi is a wasted day of their holiday since everything on the island shuts down – even the airport! Rather than despair, make like a local and take this time to stop, shut off your computer, introspect, feast your eyes on colourful parades and gatherings, and enjoy the quietude… because, according to legend, who knows who may be watching.
          </p>

          <h4>
            Become a Raw Food Chef in Ubud
          </h4>

          <p>
            Hardly a traveller makes it to Bali without spending at least a day in Ubud. While the island is famous for its family-run “warung” restaurants serving delicious and affordable local cuisine, Ubud has come to be known as a paradise destination for vegan food. When in Ubud, the Seeds of Life restaurant is an absolute must-try. Seeds of Life not only deserves accolades for its unique spin on raw vegan food, but for its Taoist Tonic Bar. This restaurant, located in the heart of the city, serves some of the finest raw vegan food (alongside delicious healthy drinks) the world over. A vitalizing tonic and SOL Bowl are perfect companions for a healthy belly, mind and soul. Once you get a taste of the unforgettable cuisine, you may even want to stay for the “Bali Raw Chef Training”, a course that will demystify the raw food phenomenon and help you on your way to becoming a raw food chef yourself.
          </p>

          <p>
            The highlight of any cooking class in Bali is that courses are usually set in a traditional Balinese open kitchen. You’ll not only learn how to create top-notch vegan dishes but will also find out you how to use “high vibe” (so Bali) herbs and spices for medicinal purposes.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt='farmers walking up their terraced farm land with palm trees at top of hill'
            className='singleBlogImage'
          />

          <h4>
            Visit a Banyan Tree
          </h4>

          <p>
            Considered as the symbol of immortality, Banyan trees are some of the most venerated trees in the world – and Bali has many of them. Bunut Bolong is a banyan tree located in Manggissari village, in the west of Bali, with a powerful local legend. The tree’s roots grow on either side of a stretch of road, forming an archway that visitors can drive through – or not. Legend has it that brides and grooms who pass through the tree’s archway will end up separated. Another road next to the tree has even been constructed for believers of the legend to pass through.
          </p>

          <p>
            Venture to the north and you’ll find another giant banyan tree near two temples called Pura Subak and Pura Pecalang and located in the village of Gesing. This tree is believed to be 700 years old, reaches 85 metres and is said to grant your wishes.
          </p>

          <p>
            If you’ve yet to experience a banyan tree in real life, its aerial roots which grow down from the branches can quite literally stop you in your tracks. Have you ever thought about visiting new locations based on where these sacred trees grow? A fun way to travel through Bali could be searching out as many banyan trees as possible and learning all the legends attached to these powerful plants.
          </p>

          <h4>
            Make your own adventure
          </h4>

          <p>
            Bali is one of those places where a holiday of a few days can easily turn into a stay of weeks, months or years – even turning into a home for some. But let’s not get ahead of ourselves. One thing is for sure: Bali is an island with seemingly endless possibilities for venturing off the beaten path. Although it’s no secret why this lush island is on so many people’s bucket lists, there are still plenty of “secret” and off-the-beaten-path locations waiting to be explored. Take a page out of DAYMADE’ book and venture off the road most travelled to create your own unique experience on one of the world’s most magical islands.
          </p>

          <Message>
            Always wanted to explore Bali yourself?! Enter the {''}
            <Link href='/' style={{ margin: 0, fontWeight: 'bold' }}>
              DAYMADE Draw
            </Link>
            {''} for a chance to win an epic trip to Bali!
          </Message>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default BaliArticle;
